import type { OnChangeFn, PaginationState, SortingState } from '@tanstack/react-table'
import { ClipboardList } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'

import { session } from '@/api/session'
import type { GroupSummary } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { DataTable, dataTableColumns, type DataTableColumn } from '@/components/data-table'
import { PageHeader } from '@/components/page-header'
import { StatusBadge } from '@/components/status-badge'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { COLLECTION_PERMISSIONS, useCollectionPermissions } from '@/features/collections/permissions'
import {
  useCollectionNavigate,
  useCollectionSearch,
  useCollectionSearchNavigate,
} from '@/features/collections/routing'
import { GroupFilters } from '@/features/groups/group-filters'
import { useGroupList } from '@/features/groups/queries'
import { formatShortDate } from '@/lib/datetime'

import {
  collectionWorklistSearchSchema,
  DEFAULT_PAGE_SIZE,
  type CollectionWorklistSearch,
} from './search'

/**
 * The collection worklist.
 *
 * A branch keys a week of paper sheets at a sitting, and the question the operator arrives with is
 * "whose sheet am I holding" — so the entry point is the group, not the meeting. Choosing one opens
 * its calendar, and a meeting on that calendar opens the sheet.
 *
 * This screen reads on a phone and does not refuse. The refusal in `docs/mobile-policy.md` is
 * against *bulk collection capture* — a dense grid of keyed amounts, where touch turns the one
 * advantage, speed, into the risk. Finding a group is a list, and a list is fine on a phone. The
 * screen that actually takes money is the one that asks for a wider display, and it does.
 *
 * Nothing here is sortable: the group list endpoint takes no ordering parameter, and ordering the
 * page that happens to be in the browser would be a claim about the whole result set that the next
 * page would contradict.
 */
const UNSORTED: SortingState = []
const ignoreSortingChange: OnChangeFn<SortingState> = () => undefined

const helper = dataTableColumns<GroupSummary>()

const columns: DataTableColumn<GroupSummary>[] = helper.columns([
  helper.accessor('groupCode', {
    header: 'Group',
    enableSorting: false,
    cell: (context) => <span className="font-mono text-xs">{context.row.original.groupCode}</span>,
    meta: { noWrap: true, widthClass: 'w-32', card: 'lead' },
  }),
  helper.accessor('name', {
    header: 'Name',
    enableSorting: false,
    cell: (context) => (
      <span className="font-medium text-heading">{context.row.original.name}</span>
    ),
    meta: { card: 'title' },
  }),
  helper.accessor('loanOfficerName', {
    header: 'Officer',
    enableSorting: false,
    // Null and unassigned are the same fact here and both are said in words: a blank cell in this
    // column reads as a name that failed to load.
    cell: (context) =>
      context.row.original.loanOfficerName ?? (
        <span className="text-xs text-muted">No officer assigned</span>
      ),
    meta: { noWrap: true },
  }),
  helper.accessor('nextMeetingDate', {
    header: 'Next meeting',
    enableSorting: false,
    cell: (context) =>
      context.row.original.nextMeetingDate === null ? (
        <span className="text-xs text-muted">Nothing scheduled</span>
      ) : (
        <span className="numeric text-xs tabular-nums">
          {formatShortDate(context.row.original.nextMeetingDate)}
        </span>
      ),
    meta: { noWrap: true, widthClass: 'w-36' },
  }),
  helper.accessor('activeMemberCount', {
    header: 'Members',
    enableSorting: false,
    cell: (context) => (
      <span className="numeric tabular-nums">{context.row.original.activeMemberCount}</span>
    ),
    meta: { align: 'right', widthClass: 'w-24' },
  }),
  helper.accessor('status', {
    header: 'Status',
    enableSorting: false,
    cell: (context) => <StatusBadge status={context.row.original.status} />,
    meta: { widthClass: 'w-32', card: 'lead' },
  }),
])

const SEARCH_DEBOUNCE_MS = 300

export function CollectionsPage() {
  const { user } = useAuth()
  const permissions = useCollectionPermissions()
  const openGroup = useCollectionNavigate()
  const navigateWithSearch = useCollectionSearchNavigate()
  const search = useCollectionSearch(collectionWorklistSearchSchema)

  const branchId = session.getBranchId() ?? user?.homeBranchId ?? null
  const [searchText, setSearchText] = useState(search.search ?? '')

  const page = search.page ?? 1
  const pageSize = search.pageSize ?? DEFAULT_PAGE_SIZE

  const updateSearch = (patch: Partial<CollectionWorklistSearch>, replace = false) => {
    navigateWithSearch(
      { screen: 'worklist' },
      {
        search: search.search,
        status: search.status,
        branchId: search.branchId,
        loanOfficerId: search.loanOfficerId,
        page: search.page,
        pageSize: search.pageSize,
        ...patch,
      },
      replace,
    )
  }

  // What this screen last wrote into the URL, so a Back button or a pasted link is mirrored into
  // the box without the debounce writing it straight back out.
  const lastPushed = useRef(search.search ?? '')

  useEffect(() => {
    const fromUrl = search.search ?? ''
    if (fromUrl !== lastPushed.current) {
      lastPushed.current = fromUrl
      setSearchText(fromUrl)
    }
  }, [search.search])

  useEffect(() => {
    const trimmed = searchText.trim()
    if (trimmed === (search.search ?? '')) {
      return
    }

    const timer = window.setTimeout(() => {
      lastPushed.current = trimmed
      navigateWithSearch(
        { screen: 'worklist' },
        {
          search: trimmed === '' ? undefined : trimmed,
          status: search.status,
          branchId: search.branchId,
          loanOfficerId: search.loanOfficerId,
          page: undefined,
          pageSize: search.pageSize,
        },
        // Typing produces one history entry for the search, not one per keystroke.
        true,
      )
    }, SEARCH_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timer)
    }
    // Every filter is a dependency, not only the text: changing one mid-keystroke restarts the
    // debounce against the new values rather than landing 300ms later and undoing the change.
  }, [
    searchText,
    search.search,
    search.status,
    search.branchId,
    search.loanOfficerId,
    search.pageSize,
    navigateWithSearch,
  ])

  const query = useGroupList({
    search: search.search,
    // Active by default. A dissolved group is not collected from, and a worklist that offered one
    // would be inviting a posting the server refuses.
    status: search.status ?? 'Active',
    branchId: search.branchId,
    loanOfficerId: search.loanOfficerId,
    page,
    pageSize,
  })

  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const current: PaginationState = { pageIndex: page - 1, pageSize }
    const next = typeof updater === 'function' ? updater(current) : updater
    updateSearch({ page: next.pageIndex + 1, pageSize: next.pageSize })
  }

  const hasActiveFilters = Boolean(
    search.search ?? search.status ?? search.branchId ?? search.loanOfficerId,
  )

  const clearFilters = () => {
    lastPushed.current = ''
    setSearchText('')
    navigateWithSearch({ screen: 'worklist' }, { pageSize: search.pageSize })
  }

  if (!permissions.canViewGroups) {
    return (
      <>
        <PageHeader title="Collection capture" />
        <Alert tone="warning">
          <AlertTitle>Collections are restricted</AlertTitle>
          <AlertDescription>
            A collection sheet is keyed against a group&apos;s meeting, so reading it needs the{' '}
            <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.groupView}</span>{' '}
            permission. Ask an administrator for it rather than working from a colleague&apos;s
            session — every posting is recorded against whoever is signed in.
          </AlertDescription>
        </Alert>
      </>
    )
  }

  return (
    <>
      <PageHeader
        title="Collection capture"
        description="Until the field application ships, every collection reaches the system through here: a branch's week of paper sheets, keyed by group, from the meeting each sheet came off."
      />

      {!permissions.canPostAnything ? (
        <Alert tone="info">
          <AlertTitle>You can read sheets but not post them</AlertTitle>
          <AlertDescription>
            Posting a collection needs at least one of{' '}
            <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.repaymentPost}</span>,{' '}
            <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.depositPost}</span>, or{' '}
            <span className="font-mono text-xs">{COLLECTION_PERMISSIONS.securityDepositTopUp}</span>
            . The sheets themselves are still worth reading — the balances and the arrears on them
            are what an officer is asked about at a meeting.
          </AlertDescription>
        </Alert>
      ) : null}

      <div className="space-y-3">
        <GroupFilters
          searchText={searchText}
          onSearchTextChange={setSearchText}
          status={search.status}
          onStatusChange={(status) => {
            updateSearch({ status, page: undefined })
          }}
          loanOfficerId={search.loanOfficerId}
          onLoanOfficerChange={(loanOfficerId) => {
            updateSearch({ loanOfficerId, page: undefined })
          }}
          branchId={branchId}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        <DataTable
          columns={columns}
          data={query.data?.items}
          rowCount={query.data?.totalCount}
          pagination={{ pageIndex: page - 1, pageSize }}
          onPaginationChange={onPaginationChange}
          sorting={UNSORTED}
          onSortingChange={ignoreSortingChange}
          isLoading={query.isPending}
          isFetching={query.isFetching}
          error={query.isError ? query.error : null}
          onRetry={() => void query.refetch()}
          getRowId={(row) => row.id}
          onRowActivate={(group) => {
            openGroup({ screen: 'groupMeetings', groupId: group.id })
          }}
          pageSizeOptions={[DEFAULT_PAGE_SIZE, 50, 100]}
          emptyTitle={hasActiveFilters ? 'No groups match' : 'No groups to collect from'}
          emptyDescription={
            hasActiveFilters
              ? 'Nothing was found with these filters. A group that has been dissolved is excluded unless you ask for it by status.'
              : 'No active group is visible at the branches you can see. A collection sheet belongs to a group meeting, so a group has to exist and have a calendar before anything can be keyed.'
          }
          emptyAction={
            hasActiveFilters ? (
              <Button variant="secondary" size="sm" onClick={clearFilters}>
                <ClipboardList />
                Clear the filters
              </Button>
            ) : null
          }
        />
      </div>
    </>
  )
}
